import React, { FC } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Alert,
  AlertDescription,
  Button,
} from '@pisaka/ui';
import DrawCardComponent from '../../../../core/components/drawCard/DrawCardComponent';
import { useModal } from '../../../../hooks/useModal';
import { MODAL_NAME_CREATE_SYMBOL } from '../../../../constants/modalNamesConstants';
import { useSaveSymbol, useSymbol } from './useSymbol';
import NewDrawCardComponent from '../../../../core/components/drawCard/NewDrawCardComponent';

const CreateSymbolModalContainer: FC = () => {
  const { t } = useTranslation();
  const { isOpenModal, onCloseModal, payload } = useModal(MODAL_NAME_CREATE_SYMBOL);

  const {
    activeSymbol, prevActiveSymbol, nextActiveSymbol, onPrevSymbol, onNextSymbol
  } = useSymbol(payload);

  const { isLoadingSaveSymbol, saveSymbol } = useSaveSymbol();

  const onSaveSvg = async (svgView: string, svgContent: string) => {
    if (activeSymbol) {
      await saveSymbol(activeSymbol.symbolId, svgView, svgContent);
      nextActiveSymbol && onNextSymbol();
    }
  };

  const isEditSymbol = !!activeSymbol?.symbolSVGView;
  const modalTitle = isEditSymbol
    ? t('fontCreator.editSymbol', { name: activeSymbol?.symbolName })
    : t('fontCreator.createSymbol', { name: activeSymbol?.symbolName });

  const { search } = window.location;
  const isShowNewCard = search.includes('isNewCard');

  return (
    <Dialog open={isOpenModal} onOpenChange={(open) => !open && onCloseModal()}>
      <DialogContent className="w-[90vw] max-w-2xl">
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>{t('fontCreator.symbolWarningTitle')}</strong>{' '}
              {t('fontCreator.symbolWarning')}
            </AlertDescription>
          </Alert>

          <div className="relative flex justify-center">
            {prevActiveSymbol && (
              <button
                type="button"
                onClick={onPrevSymbol}
                className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5 z-10 bg-gradient-accent hover:bg-accent shadow-accent text-primary-foreground rounded-md px-2 py-1"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="text-xs">{prevActiveSymbol.symbolName}</span>
              </button>
            )}

            {activeSymbol && (
              isShowNewCard ? (
                <NewDrawCardComponent
                  symbol={activeSymbol.symbolName}
                  onSaveSvg={onSaveSvg}
                  svgString={activeSymbol?.symbolSVGView}
                  isLoadingSaveSymbol={isLoadingSaveSymbol}
                />
              ) : (
                <DrawCardComponent
                  symbol={activeSymbol.symbolName}
                  onSaveSvg={onSaveSvg}
                  svgString={activeSymbol?.symbolSVGView}
                  isLoadingSaveSymbol={isLoadingSaveSymbol}
                />
              )
            )}

            {nextActiveSymbol && (
              <button
                type="button"
                onClick={onNextSymbol}
                className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5 z-10 bg-gradient-accent hover:bg-accent shadow-accent text-primary-foreground rounded-md px-2 py-1"
              >
                <ChevronRight className="w-5 h-5" />
                <span className="text-xs">{nextActiveSymbol.symbolName}</span>
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSymbolModalContainer;
